import React from 'react';
import {
  Compass as CompassIcon, Moon, Sun, Sparkles, Calculator,
  UserCheck, Flame, Clock, Scale, Grid, Heart, Shield
} from 'lucide-react';

export type CalculatorCategoryKey =
  | 'vedicCore'
  | 'compatibility'
  | 'doshasAndTransits'
  | 'advancedSystems'
  | 'numerologyAndFun';

export type CalculatorNavGroup =
  | 'signsBirthChart'
  | 'doshasDasha'
  | 'matchingJaimini'
  | 'advancedSystems'
  | 'panchangTiming'
  | 'coreNumerology'
  | 'luckyNumberChecks';

export interface CalculatorItem {
  id: string;
  href: string;
  categoryKey: CalculatorCategoryKey;
  category: string; // English category name for backward compat
  label: string; // English name for backward compat
  description: string; // English description for backward compat
  isTopPriority?: boolean;
  iconName: string;
  navGroup: CalculatorNavGroup;
}

export interface NavGroupConfig {
  key: CalculatorNavGroup;
  titleKey: string;
  defaultTitle: string;
  column: 1 | 2 | 3;
}

export const CALCULATOR_NAV_GROUPS: NavGroupConfig[] = [
  // Column 1
  { key: 'signsBirthChart', titleKey: 'signsBirthChart', defaultTitle: 'Signs & Birth Chart', column: 1 },
  { key: 'doshasDasha', titleKey: 'doshasDasha', defaultTitle: 'Dosha & Dasha', column: 1 },
  // Column 2
  { key: 'matchingJaimini', titleKey: 'matchingJaimini', defaultTitle: 'Matching & Jaimini', column: 2 },
  { key: 'advancedSystems', titleKey: 'advancedSystems', defaultTitle: 'Advanced Systems', column: 2 },
  { key: 'panchangTiming', titleKey: 'panchangTiming', defaultTitle: 'Panchang & Timing', column: 2 },
  // Column 3
  { key: 'coreNumerology', titleKey: 'coreNumerology', defaultTitle: 'Core Numerology', column: 3 },
  { key: 'luckyNumberChecks', titleKey: 'luckyNumberChecks', defaultTitle: 'Lucky Number Checks', column: 3 },
];

export const CALCULATOR_CATEGORIES: { key: CalculatorCategoryKey; label: string }[] = [
  { key: 'vedicCore', label: 'Vedic Core' },
  { key: 'compatibility', label: 'Compatibility' },
  { key: 'doshasAndTransits', label: 'Doshas & Transits' },
  { key: 'advancedSystems', label: 'Advanced Systems' },
  { key: 'numerologyAndFun', label: 'Numerology & Fun' },
];

export const CALCULATOR_ITEMS: CalculatorItem[] = [
  // --- Column 1: Signs & Birth Chart (9 items) ---
  {
    id: 'birth-chart',
    href: '/birth-chart-calculator',
    categoryKey: 'vedicCore',
    category: 'Vedic Core',
    label: 'Birth Chart Calculator',
    description: 'Generate complete Vedic Kundli chart with planetary positions',
    isTopPriority: true,
    iconName: 'Compass',
    navGroup: 'signsBirthChart'
  },
  {
    id: 'moon-sign',
    href: '/moon-sign-calculator',
    categoryKey: 'vedicCore',
    category: 'Vedic Core',
    label: 'Moon Sign Calculator',
    description: 'Find your Vedic Chandra Rashi and emotional nature',
    isTopPriority: true,
    iconName: 'Moon',
    navGroup: 'signsBirthChart'
  },
  {
    id: 'rashi-sign',
    href: '/rashi-sign-calculator',
    categoryKey: 'vedicCore',
    category: 'Vedic Core',
    label: 'Rashi Sign Calculator',
    description: 'Find your Janma Rashi (Vedic Chandra Rashi terminology)',
    iconName: 'Moon',
    navGroup: 'signsBirthChart'
  },
  {
    id: 'sun-sign',
    href: '/sun-sign-calculator',
    categoryKey: 'vedicCore',
    category: 'Vedic Core',
    label: 'Sun Sign Calculator',
    description: 'Find your Vedic Surya Rashi calculated with ayanamsa',
    iconName: 'Sun',
    navGroup: 'signsBirthChart'
  },
  {
    id: 'ascendant',
    href: '/ascendant-calculator',
    categoryKey: 'vedicCore',
    category: 'Vedic Core',
    label: 'Ascendant Calculator',
    description: 'Calculate your exact Lagna sign and Lagna nakshatra',
    iconName: 'Compass',
    navGroup: 'signsBirthChart'
  },
  {
    id: 'nakshatra',
    href: '/nakshatra-calculator',
    categoryKey: 'vedicCore',
    category: 'Vedic Core',
    label: 'Nakshatra Calculator',
    description: 'Discover your Janma Nakshatra, Pada, and Nakshatra Lord',
    iconName: 'Sparkles',
    navGroup: 'signsBirthChart'
  },
  {
    id: 'navamsa-chart',
    href: '/navamsa-chart-calculator',
    categoryKey: 'vedicCore',
    category: 'Vedic Core',
    label: 'Navamsa Chart Calculator',
    description: 'Calculate D9 Navamsa divisional chart, Navamsa Lagna & Vargottama planets',
    iconName: 'Grid',
    navGroup: 'signsBirthChart'
  },
  {
    id: 'western-zodiac',
    href: '/western-zodiac-calculator',
    categoryKey: 'vedicCore',
    category: 'Vedic Core',
    label: 'Western Zodiac Calculator',
    description: 'Find your tropical Sun sign, traits, element & ruling planet',
    iconName: 'Calculator',
    navGroup: 'signsBirthChart'
  },
  {
    id: 'moon-phase',
    href: '/moon-phase-calculator',
    categoryKey: 'vedicCore',
    category: 'Vedic Core',
    label: 'Moon Phase Calculator',
    description: 'Find your Moon phase at birth: illumination percentage, elongation, tithi & paksha',
    iconName: 'Moon',
    navGroup: 'signsBirthChart'
  },

  // --- Column 1: Dosha & Dasha (6 items) ---
  {
    id: 'mangal-dosha',
    href: '/mangal-dosha-calculator',
    categoryKey: 'doshasAndTransits',
    category: 'Doshas & Transits',
    label: 'Mangal Dosha Calculator',
    description: 'Check Kuja / Mangal Dosha in Lagna & Moon chart',
    isTopPriority: true,
    iconName: 'Flame',
    navGroup: 'doshasDasha'
  },
  {
    id: 'kaal-sarp',
    href: '/kaal-sarp-calculator',
    categoryKey: 'doshasAndTransits',
    category: 'Doshas & Transits',
    label: 'Kaal Sarp Dosh Calculator',
    description: 'Check Kaal Sarp Dosh alignment & 12 house classifications',
    isTopPriority: true,
    iconName: 'Compass',
    navGroup: 'doshasDasha'
  },
  {
    id: 'sade-sati',
    href: '/sade-sati-calculator',
    categoryKey: 'doshasAndTransits',
    category: 'Doshas & Transits',
    label: 'Sade Sati Calculator',
    description: 'Saturn 7.5-year transit cycle status & ephemeris date ranges',
    iconName: 'Clock',
    navGroup: 'doshasDasha'
  },
  {
    id: 'pitra-dosha',
    href: '/pitra-dosha-calculator',
    categoryKey: 'doshasAndTransits',
    category: 'Doshas & Transits',
    label: 'Pitra Dosha Calculator',
    description: 'Evaluate classical Vedic Pitra Dosha indicators, mitigating factors (Dosha Bhanga) & ancestral guidance',
    iconName: 'Shield',
    navGroup: 'doshasDasha'
  },
  {
    id: 'dasha',
    href: '/dasha-calculator',
    categoryKey: 'vedicCore',
    category: 'Vedic Core',
    label: 'Dasha Calculator',
    description: '120-Year Vimshottari Mahadasha timeline & active period',
    iconName: 'Clock',
    navGroup: 'doshasDasha'
  },
  {
    id: 'transit-chart',
    href: '/transit-chart-calculator',
    categoryKey: 'doshasAndTransits',
    category: 'Doshas & Transits',
    label: 'Transit Chart Calculator',
    description: 'Overlay current transits onto natal houses & detect conjunctions',
    iconName: 'Compass',
    navGroup: 'doshasDasha'
  },

  // --- Column 2: Matching & Jaimini (7 items) ---
  {
    id: 'kundli-matching',
    href: '/kundli-matching',
    categoryKey: 'compatibility',
    category: 'Compatibility',
    label: 'Kundli Matching',
    description: '36-Point Ashtakoot Guna Milan for marital compatibility',
    isTopPriority: true,
    iconName: 'Heart',
    navGroup: 'matchingJaimini'
  },
  {
    id: 'marriage-compatibility',
    href: '/marriage-compatibility-calculator',
    categoryKey: 'compatibility',
    category: 'Compatibility',
    label: 'Marriage Compatibility Calculator',
    description: '36-Point Ashtakoot Guna Milan framed for marriage decisions',
    iconName: 'Heart',
    navGroup: 'matchingJaimini'
  },
  {
    id: 'nadi-dosha',
    href: '/nadi-dosha-calculator',
    categoryKey: 'compatibility',
    category: 'Compatibility',
    label: 'Nadi Dosha Calculator',
    description: 'Standalone Nadi Dosha evaluation & Nivarana exception checker',
    iconName: 'Heart',
    navGroup: 'matchingJaimini'
  },
  {
    id: 'zodiac-compatibility',
    href: '/compatibility',
    categoryKey: 'compatibility',
    category: 'Compatibility',
    label: 'Zodiac Compatibility',
    description: 'Quick Western zodiac Sun-sign pair compatibility rating',
    iconName: 'Heart',
    navGroup: 'matchingJaimini'
  },
  {
    id: 'love-calculator',
    href: '/love-calculator',
    categoryKey: 'numerologyAndFun',
    category: 'Numerology & Fun',
    label: 'Love Calculator',
    description: 'Recreational name-based love and compatibility score based on Vedic phonetic Nakshatras',
    iconName: 'Heart',
    navGroup: 'matchingJaimini'
  },
  {
    id: 'flames',
    href: '/flames-calculator',
    categoryKey: 'numerologyAndFun',
    category: 'Numerology & Fun',
    label: 'FLAMES Calculator (Fun)',
    description: 'Classic name-matching game (Just for Fun)',
    iconName: 'Heart',
    navGroup: 'matchingJaimini'
  },
  {
    id: 'atmakaraka',
    href: '/atmakaraka-calculator',
    categoryKey: 'vedicCore',
    category: 'Vedic Core',
    label: 'Atmakaraka Calculator',
    description: 'Calculate your Jaimini Atmakaraka (Soul Planet), Darakaraka & full 7 Chara Karaka ranking',
    iconName: 'Sun',
    navGroup: 'matchingJaimini'
  },

  // --- Column 2: Advanced Systems (4 items) ---
  {
    id: 'ashtakavarga',
    href: '/ashtakavarga-calculator',
    categoryKey: 'advancedSystems',
    category: 'Advanced Systems',
    label: 'Ashtakavarga Calculator',
    description: 'Parashari Bhinnashtakavarga & Sarvashtakavarga bindu charts',
    iconName: 'Calculator',
    navGroup: 'advancedSystems'
  },
  {
    id: 'shadbala',
    href: '/shadbala-calculator',
    categoryKey: 'advancedSystems',
    category: 'Advanced Systems',
    label: 'Shadbala Calculator',
    description: '6-Component planetary strength (Sthana, Dig, Kala, Chesta, Naisargika, Drik)',
    iconName: 'Scale',
    navGroup: 'advancedSystems'
  },
  {
    id: 'yantra',
    href: '/yantra-calculator',
    categoryKey: 'advancedSystems',
    category: 'Advanced Systems',
    label: 'Yantra Calculator',
    description: 'Navagraha magic square library & Shadbala-driven recommendation',
    iconName: 'Grid',
    navGroup: 'advancedSystems'
  },
  {
    id: 'ayanamsa-comparison',
    href: '/ayanamsa-comparison-calculator',
    categoryKey: 'advancedSystems',
    category: 'Advanced Systems',
    label: 'Ayanamsa Comparison Calculator',
    description: 'Side-by-side precision comparison of Lahiri, Raman & KP Ayanamsa values',
    iconName: 'Scale',
    navGroup: 'advancedSystems'
  },

  // --- Column 2: Panchang & Timing (5 items) ---
  {
    id: 'birth-panchang',
    href: '/birth-panchang-calculator',
    categoryKey: 'vedicCore',
    category: 'Vedic Core',
    label: 'Birth Panchang Calculator',
    description: 'Calculate 5 Panchang elements (Tithi, Nakshatra, Yoga, Karana, Vara) at birth moment',
    iconName: 'Sparkles',
    navGroup: 'panchangTiming'
  },
  {
    id: 'rahu-kaal',
    href: '/rahu-kaal-calculator',
    categoryKey: 'doshasAndTransits',
    category: 'Doshas & Transits',
    label: 'Rahu Kaal Calculator',
    description: 'Inauspicious daily Rahu Kaal window calculation for your location',
    iconName: 'Clock',
    navGroup: 'panchangTiming'
  },
  {
    id: 'choghadiya',
    href: '/choghadiya-calculator',
    categoryKey: 'doshasAndTransits',
    category: 'Doshas & Transits',
    label: 'Choghadiya Calculator',
    description: 'Auspicious & inauspicious Day/Night Choghadiya time blocks',
    iconName: 'Clock',
    navGroup: 'panchangTiming'
  },
  {
    id: 'hora',
    href: '/hora-calculator',
    categoryKey: 'doshasAndTransits',
    category: 'Doshas & Transits',
    label: 'Hora Calculator',
    description: '24 Hour-by-hour planetary rulership cycles for favorable actions',
    iconName: 'Clock',
    navGroup: 'panchangTiming'
  },
  {
    id: 'baby-name',
    href: '/baby-name-calculator',
    categoryKey: 'vedicCore',
    category: 'Vedic Core',
    label: 'Baby Name Calculator',
    description: 'Find auspicious Vedic baby naming syllables (Nama Nakshatra) based on birth Nakshatra & Pada',
    iconName: 'Sparkles',
    navGroup: 'panchangTiming'
  },

  // --- Column 3: Core Numerology (5 items) ---
  {
    id: 'numerology',
    href: '/numerology-calculator',
    categoryKey: 'numerologyAndFun',
    category: 'Numerology & Fun',
    label: 'Numerology Calculator',
    description: 'Calculate Life Path Number and Birthday Number from DOB',
    isTopPriority: true,
    iconName: 'Sparkles',
    navGroup: 'coreNumerology'
  },
  {
    id: 'name-numerology',
    href: '/name-numerology-calculator',
    categoryKey: 'numerologyAndFun',
    category: 'Numerology & Fun',
    label: 'Name Numerology Calculator',
    description: 'Calculate Expression, Soul Urge, and Personality numbers',
    iconName: 'UserCheck',
    navGroup: 'coreNumerology'
  },
  {
    id: 'personal-year-number',
    href: '/personal-year-number-calculator',
    categoryKey: 'numerologyAndFun',
    category: 'Numerology & Fun',
    label: 'Personal Year Calculator',
    description: 'Find your annual numerology cycle and 12-month spiritual growth themes',
    iconName: 'Clock',
    navGroup: 'coreNumerology'
  },
  {
    id: 'lo-shu-grid',
    href: '/lo-shu-grid-calculator',
    categoryKey: 'numerologyAndFun',
    category: 'Numerology & Fun',
    label: 'Lo Shu Grid Calculator',
    description: 'Generate ancient Chinese 3x3 Magic Square from birth date to reveal missing numbers',
    iconName: 'Grid',
    navGroup: 'coreNumerology'
  },
  {
    id: 'name-correction-numerology',
    href: '/name-correction-numerology-calculator',
    categoryKey: 'numerologyAndFun',
    category: 'Numerology & Fun',
    label: 'Name Correction Numerology',
    description: 'Analyze name vibration against Radical and Life Path numbers for harmonious alignment',
    iconName: 'Sparkles',
    navGroup: 'coreNumerology'
  },

  // --- Column 3: Lucky Number Checks (4 items) ---
  {
    id: 'mobile-number-numerology',
    href: '/mobile-number-numerology-calculator',
    categoryKey: 'numerologyAndFun',
    category: 'Numerology & Fun',
    label: 'Mobile Number Numerology',
    description: 'Evaluate digit total, compound vibration and compatibility with your radical number',
    iconName: 'Calculator',
    navGroup: 'luckyNumberChecks'
  },
  {
    id: 'vehicle-number-numerology',
    href: '/vehicle-number-numerology-calculator',
    categoryKey: 'numerologyAndFun',
    category: 'Numerology & Fun',
    label: 'Vehicle Number Numerology',
    description: 'Calculate registration number digit sum and planetary resonance with birth energy',
    iconName: 'Calculator',
    navGroup: 'luckyNumberChecks'
  },
  {
    id: 'house-number-numerology',
    href: '/house-number-numerology-calculator',
    categoryKey: 'numerologyAndFun',
    category: 'Numerology & Fun',
    label: 'House Number Numerology',
    description: 'Discover your home or apartment number planetary vibration and resident harmony',
    iconName: 'Calculator',
    navGroup: 'luckyNumberChecks'
  },
  {
    id: 'business-name-numerology',
    href: '/business-name-numerology-calculator',
    categoryKey: 'numerologyAndFun',
    category: 'Numerology & Fun',
    label: 'Business Name Numerology',
    description: 'Calculate brand and commercial name vibrations with Chaldean or Pythagorean tables',
    iconName: 'Sparkles',
    navGroup: 'luckyNumberChecks'
  }
];

export function getCalculatorsByNavGroup(groupKey: CalculatorNavGroup): CalculatorItem[] {
  return CALCULATOR_ITEMS.filter(item => item.navGroup === groupKey);
}

export function renderCalculatorIcon(iconName: string, className = "w-5 h-5 text-amber-400"): React.ReactNode {
  switch (iconName) {
    case 'Moon': return <Moon className={className} />;
    case 'Sun': return <Sun className={className} />;
    case 'Sparkles': return <Sparkles className={className} />;
    case 'Calculator': return <Calculator className={className} />;
    case 'UserCheck': return <UserCheck className={className} />;
    case 'Flame': return <Flame className={className} />;
    case 'Clock': return <Clock className={className} />;
    case 'Scale': return <Scale className={className} />;
    case 'Grid': return <Grid className={className} />;
    case 'Heart': return <Heart className={className} />;
    case 'Shield': return <Shield className={className} />;
    case 'Compass':
    default:
      return <CompassIcon className={className} />;
  }
}
